import * as React from "react";
import { Section } from "./TermsContent";

const LAST_UPDATED_ISO = "2025-07-01";

export const PrivacyContent: React.FC<{ withPageTitle?: boolean }> = ({
  withPageTitle = true,
}) => {
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString("pt-BR");

  return (
    <div>
      {withPageTitle && (
        <>
          <h1 className="text-3xl font-bold mb-4">
            Política de Privacidade da Nacional GO
          </h1>
          <p className="text-sm text-n-text-neutral mb-8">
            Última atualização: {lastUpdated}
          </p>
        </>
      )}

      <Section title="1. Apresentação">
        <p>
          A NACIONAL GO TECNOLOGIA LTDA, inscrita no CNPJ sob nº
          46.951.207/0001-42, com sede na Rodovia José Carlos Daux, 4150,
          Primavera Office, Sala 102, Saco Grande, Florianópolis/SC, CEP
          88.032-005 ("Nacional GO"), no papel de controladora de dados, se
          compromete a preservar a privacidade dos usuários de sua plataforma e
          a tratar seus dados pessoais em conformidade com a Lei nº 13.709/2018
          (Lei Geral de Proteção de Dados — LGPD) e demais normas aplicáveis.
        </p>
        <p>
          Esta Política de Privacidade descreve como coletamos, usamos,
          armazenamos, compartilhamos e protegemos as informações pessoais dos
          nossos usuários.
        </p>
      </Section>

      <Section title="2. Dados Coletados">
        <p>Podemos coletar as seguintes categorias de dados pessoais:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Dados de identificação:</strong> nome completo, CPF/CNPJ,
            RG, data de nascimento.
          </li>
          <li>
            <strong>Dados de contato:</strong> endereço de e-mail, telefone,
            endereço residencial ou comercial.
          </li>
          <li>
            <strong>Dados do veículo:</strong> placa, chassi, modelo, ano,
            marca e estado de conservação.
          </li>
          <li>
            <strong>Dados de pagamento:</strong> informações de cartão de
            crédito (processadas por gateway seguro), dados bancários para
            reembolso.
          </li>
          <li>
            <strong>Dados de uso da plataforma:</strong> endereço IP, tipo de
            navegador, sistema operacional, páginas acessadas e interações.
          </li>
          <li>
            <strong>Dados de comunicação:</strong> histórico de atendimentos,
            chamados e mensagens trocadas com o suporte.
          </li>
        </ul>
      </Section>

      <Section title="3. Finalidade do Tratamento">
        <p>Os dados pessoais coletados são utilizados para:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Prestação dos serviços contratados de transporte de veículos;</li>
          <li>Gestão de conta, contratos e histórico de pedidos;</li>
          <li>Comunicação sobre o status do serviço;</li>
          <li>Processamento de pagamentos e emissão de documentos fiscais;</li>
          <li>Cumprimento de obrigações legais e regulatórias;</li>
          <li>Prevenção a fraudes e proteção da segurança da plataforma;</li>
          <li>
            Envio de comunicações de marketing, mediante consentimento do
            usuário;
          </li>
          <li>Melhoria contínua dos serviços e experiência do usuário.</li>
        </ul>
      </Section>

      <Section title="4. Base Legal para o Tratamento">
        <p>
          O tratamento de dados pessoais pela Nacional GO é fundamentado nas
          seguintes bases legais previstas na LGPD:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Execução de contrato</strong> (art. 7º, V): para prestação
            dos serviços contratados.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal</strong> (art. 7º, II): para
            atender exigências fiscais, tributárias e regulatórias.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX): para prevenção a
            fraudes e segurança da plataforma.
          </li>
          <li>
            <strong>Consentimento</strong> (art. 7º, I): para envio de
            comunicações de marketing e uso de cookies não essenciais.
          </li>
        </ul>
      </Section>

      <Section title="5. Compartilhamento de Dados">
        <p>
          A Nacional GO pode compartilhar dados pessoais com terceiros nas
          seguintes situações:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Transportadores parceiros:</strong> dados necessários para a
            execução do transporte (nome, contato, endereços e dados do
            veículo).
          </li>
          <li>
            <strong>Processadores de pagamento:</strong> gateways de pagamento
            e instituições financeiras.
          </li>
          <li>
            <strong>Prestadores de serviços de TI:</strong> servidores em
            nuvem, suporte técnico e ferramentas de analytics.
          </li>
          <li>
            <strong>Seguradoras:</strong> em caso de acionamento do seguro
            contratado.
          </li>
          <li>
            <strong>Autoridades públicas:</strong> quando exigido por lei,
            decisão judicial ou regulação.
          </li>
        </ul>
        <p>
          Não comercializamos dados pessoais de usuários a terceiros para fins
          publicitários.
        </p>
      </Section>

      <Section title="6. Retenção e Exclusão de Dados">
        <p>
          Os dados pessoais são mantidos pelo período necessário ao cumprimento
          das finalidades descritas nesta Política ou pelo prazo exigido por
          lei.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Dados contratuais: mantidos por até 5 anos após o término do
            contrato, conforme Código Civil;
          </li>
          <li>
            Dados fiscais: mantidos pelo prazo exigido pela legislação
            tributária (até 10 anos);
          </li>
          <li>
            Dados de marketing: mantidos até a revogação do consentimento pelo
            usuário.
          </li>
        </ul>
      </Section>

      <Section title="7. Direitos do Titular dos Dados">
        <p>
          Nos termos da LGPD, o usuário possui os seguintes direitos em relação
          aos seus dados pessoais:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Confirmação da existência de tratamento;</li>
          <li>Acesso aos dados;</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
          <li>
            Anonimização, bloqueio ou eliminação de dados desnecessários ou
            excessivos;
          </li>
          <li>
            Portabilidade dos dados a outro fornecedor de serviço ou produto;
          </li>
          <li>
            Eliminação dos dados pessoais tratados com base no consentimento;
          </li>
          <li>
            Informação sobre compartilhamento com terceiros (públicos e
            privados);
          </li>
          <li>Revogação do consentimento a qualquer momento.</li>
        </ul>
        <p>
          Para exercer seus direitos, entre em contato pelo e-mail:{" "}
          <a
            href="mailto:privacidade@nacionalgo.com.br"
            className="underline hover:text-n-text-green"
          >
            privacidade@nacionalgo.com.br
          </a>
          .
        </p>
      </Section>

      <Section title="8. Segurança dos Dados">
        <p>
          A Nacional GO adota medidas técnicas e organizacionais para proteger
          os dados pessoais contra acesso não autorizado, perda, alteração,
          divulgação ou destruição, incluindo:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Criptografia de dados em trânsito (TLS/HTTPS);</li>
          <li>Controle de acesso baseado em perfis e funções;</li>
          <li>Monitoramento e auditoria de acessos;</li>
          <li>Treinamento periódico da equipe sobre proteção de dados.</li>
        </ul>
        <p>
          Em caso de incidente de segurança que possa afetar seus dados,
          notificaremos os titulares e a Autoridade Nacional de Proteção de
          Dados (ANPD) conforme previsto em lei.
        </p>
      </Section>

      <Section title="9. Transferência Internacional de Dados">
        <p>
          Alguns de nossos prestadores de serviços de tecnologia podem estar
          localizados fora do Brasil. Nestes casos, garantimos que a
          transferência ocorre com salvaguardas adequadas, em conformidade com
          os requisitos da LGPD e orientações da ANPD.
        </p>
      </Section>

      <Section title="10. Encarregado de Proteção de Dados (DPO)">
        <p>
          A Nacional GO designou um Encarregado de Proteção de Dados (DPO)
          responsável por atender requisições de titulares e comunicar-se com a
          ANPD. Para contato:
        </p>
        <ul className="list-disc pl-5">
          <li>
            E-mail:{" "}
            <a
              href="mailto:privacidade@nacionalgo.com.br"
              className="underline hover:text-n-text-green"
            >
              privacidade@nacionalgo.com.br
            </a>
          </li>
        </ul>
      </Section>

      <Section title="11. Atualizações desta Política">
        <p>
          Esta Política de Privacidade pode ser atualizada periodicamente. A
          versão vigente estará sempre disponível nesta página com a data da
          última revisão. Recomendamos consulta regular.
        </p>
        <p>
          O uso continuado da plataforma após alterações implica aceitação da
          Política revisada.
        </p>
      </Section>

      <Section title="12. Foro e Legislação Aplicável">
        <p>
          Esta Política é regida pela legislação brasileira, especialmente pela
          Lei nº 13.709/2018 (LGPD). Fica eleito o foro da Comarca de
          Florianópolis/SC para dirimir quaisquer controvérsias.
        </p>
      </Section>
    </div>
  );
};
